import { useCallback } from 'react';

import { atom, useAtomValue } from 'jotai';
import startCase from 'lodash/startCase';

import { remoteI18nMapsAtom, remoteI18nMarkdownMapsAtom, userLocaleAtom } from '@store';

import { flatten } from './helpers';
import { I18N, I18N_LOCALE_MAPS, I18N_LOCALES } from './locales';

const LOCALE_FLAT_MAP = Object.fromEntries(
    I18N_LOCALES.map((key) => {
        const localeMap = I18N_LOCALE_MAPS[key];
        return [key, flatten(localeMap)];
    }),
);

const DEFAULT_LOCALE = 'en_US';
const localeMap = {
    en: 'en_US',
    is: 'is_IS',
} as const;

/**
 * TODO: Use from user settings, when we create an option to select it.
 */
const currentLocaleAtom = atom<I18N>((get) => {
    const localeKey = get(userLocaleAtom) ?? 'en';
    return localeMap[localeKey] ?? DEFAULT_LOCALE;
});

const translationDefaultsAtom = atom((get) => {
    const currentLocaleSetting = get(currentLocaleAtom);
    const translations = LOCALE_FLAT_MAP[currentLocaleSetting];
    return translations;
});

const remoteTranslationsAtom = atom((get) => {
    const currentLocaleSetting = get(currentLocaleAtom);
    const remoteTranslationMaps = get(remoteI18nMapsAtom);
    return remoteTranslationMaps?.[currentLocaleSetting];
});

const remoteMarkdownTranslationsAtom = atom((get) => {
    const currentLocaleSetting = get(currentLocaleAtom);
    const remoteTranslationMaps = get(remoteI18nMarkdownMapsAtom);
    return remoteTranslationMaps?.[currentLocaleSetting];
});

const translationsAtom = atom((get) => {
    const translationDefaults = get(translationDefaultsAtom);
    const remoteTranslations = get(remoteTranslationsAtom) || {};
    const remoteMarkdownTranslations = get(remoteMarkdownTranslationsAtom) || {};

    return { ...translationDefaults, ...remoteTranslations, ...remoteMarkdownTranslations };
});

const FALLBACK_TRANSLATIONS_MAP = LOCALE_FLAT_MAP[DEFAULT_LOCALE];

type TranslationKey = keyof typeof FALLBACK_TRANSLATIONS_MAP;

export function useTranslate(): (key: string) => string;
export function useTranslate(): (key: TranslationKey) => string;
export function useTranslate(): (key: TranslationKey | string) => string {
    const translationsMap = useAtomValue(translationsAtom);

    const translate = useCallback(
        // we add "| string" to allow dynamic mapping
        (translationKey: TranslationKey | string): string => {
            const translation = translationsMap[translationKey as TranslationKey];
            if (translation) return translation;
            if (translationKey !== '-') console.error(`Missing translation for key: ${translationKey}`);
            const fallback = FALLBACK_TRANSLATIONS_MAP[translationKey as TranslationKey];
            if (fallback) return fallback;
            if (translationKey !== '-') console.error(`Key does not exist: ${translationKey}`);
            return startCase(translationKey);
        },
        [translationsMap],
    );

    return translate;
}
